import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { BigDoneIcon, BigInfoIcon } from '../components/icons.js';
import { Button, Card, LinkButton, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime } from '../i18n.js';
import { ME_KEY, useMe } from '../session.js';

/** Statuses of a subscription that keep the account from being deleted (as the API checks them). */
const BILLING_STATUSES = ['trialing', 'active', 'past_due', 'unpaid'];

/** US-11 on the account page (account.html): "Daten exportieren" and "Konto löschen". */
export function PrivacyCards() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const client = useQueryClient();
  const navigate = useNavigate();
  const timeZone = me.data?.account.timezone;
  const billing = useQuery({ queryKey: ['billing'], queryFn: () => unwrap(api.GET('/billing')) });
  const sub = billing.data?.subscription;
  // A running subscription that renews: the API refuses the deletion, so the card says so at once
  // (audit, 10.6).
  const subscribed =
    sub !== null &&
    sub !== undefined &&
    !sub.cancelAtPeriodEnd &&
    BILLING_STATUSES.includes(sub.status);

  const exportData = useMutation({
    mutationFn: () => unwrap(api.POST('/account/export')),
  });

  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');
  // The password takes the focus; on second thought it goes back to the button.
  const passwordField = useRef<HTMLInputElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const [kept, setKept] = useState(false);
  useEffect(() => {
    if (confirming) passwordField.current?.focus();
    else if (kept) deleteButton.current?.focus();
  }, [confirming, kept]);
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE('/account', { body: { password } })),
    onSuccess: (data) => {
      client.setQueryData(ME_KEY, null);
      client.clear();
      void navigate(`/account-deleted?until=${encodeURIComponent(data.deleteAt)}`, {
        replace: true,
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    remove.mutate();
  };
  const removeError =
    remove.error instanceof ApiError && remove.error.code === 'CONFLICT'
      ? t('privacy.delete.subscription')
      : remove.error instanceof ApiError && remove.error.code === 'INVALID_CREDENTIALS'
        ? t('privacy.delete.wrongPassword')
        : remove.error
          ? errorMessage(remove.error, t)
          : undefined;

  return (
    <>
      <Card>
        <h2>
          {t('privacy.export.title')} <small>{t('privacy.export.text')}</small>
        </h2>
        {exportData.isSuccess ? (
          <Notice kind="success" title={t('privacy.export.sentTitle')}>
            {t('privacy.export.sentText', {
              date: formatDateTime(exportData.data.expiresAt, i18n.language, timeZone),
            })}
          </Notice>
        ) : null}
        {exportData.isError ? (
          <Notice kind="error">
            {exportData.error instanceof ApiError && exportData.error.code === 'RATE_LIMITED'
              ? t('privacy.export.once')
              : errorMessage(exportData.error, t)}
          </Notice>
        ) : null}
        <div className="acts">
          <Button
            variant="secondary"
            busy={exportData.isPending}
            disabled={exportData.isSuccess}
            onClick={() => exportData.mutate()}
          >
            {t('privacy.export.button')}
          </Button>
        </div>
      </Card>
      <Card className="danger-zone">
        <h2>
          {t('privacy.delete.title')}{' '}
          <small>{`${t('privacy.delete.text')} ${t('privacy.delete.undo')}`}</small>
        </h2>
        {subscribed ? (
          <Notice kind="info" title={t('privacy.delete.subscriptionTitle')}>
            {t('privacy.delete.subscription')}
          </Notice>
        ) : null}
        {confirming ? (
          <form onSubmit={submit} className="confirm">
            <div className="field-narrow">
              <TextField
                ref={passwordField}
                label={t('privacy.delete.password')}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={removeError}
                required
              />
            </div>
            <div className="acts">
              <Button type="submit" variant="danger" busy={remove.isPending}>
                {t('privacy.delete.confirm')}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setPassword('');
                  remove.reset();
                  setKept(true);
                  setConfirming(false);
                }}
              >
                {t('app.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <div className="acts">
            <Button
              ref={deleteButton}
              variant="danger"
              onClick={() => {
                setKept(false);
                setConfirming(true);
              }}
            >
              {t('privacy.delete.button')}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}

/** One card in the middle of the page (privacy.html .centered .card). */
function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="centered">
      <div className="card">{children}</div>
    </div>
  );
}

/**
 * The title of a card that replaced the button just pressed: it takes the focus, so a screen reader
 * reads what happened instead of falling back to the top of the page.
 */
function DoneTitle({ children }: { children: string }) {
  const title = useRef<HTMLHeadingElement>(null);
  useEffect(() => title.current?.focus(), []);
  return (
    <h1 ref={title} tabIndex={-1}>
      {children}
    </h1>
  );
}

/** After the deletion (signed out): what happens next (privacy.html, state "deleted"). */
export function AccountDeleted() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const until = params.get('until');
  return (
    <Centered>
      <BigInfoIcon />
      <h1>{t('privacy.deleted.title')}</h1>
      <p>
        {until && !Number.isNaN(Date.parse(until))
          ? t('privacy.deleted.text', { date: formatDate(until, i18n.language) })
          : t('privacy.deleted.textNoDate')}
      </p>
      <LinkButton to="/login" variant="secondary">
        {t('privacy.toLogin')}
      </LinkButton>
    </Centered>
  );
}

/** /restore-account?token=… from the deletion e-mail (privacy.html, "restore" and "restored"). */
export function RestoreAccount() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const restore = useMutation({
    mutationFn: () => unwrap(api.POST('/account/restore', { body: { token } })),
  });
  if (restore.isSuccess)
    return (
      <Centered>
        <BigDoneIcon />
        <DoneTitle>{t('privacy.restore.doneTitle')}</DoneTitle>
        {/* Within the plan (audit, 10.6; SP-A2.57): sites beyond it stay paused. */}
        <p>{t('privacy.restore.doneText')}</p>
        <div className="acts">
          <LinkButton to="/login">{t('privacy.toLogin')}</LinkButton>
          <LinkButton to="/billing" variant="secondary">
            {t('billing.title')}
          </LinkButton>
        </div>
      </Centered>
    );
  return (
    <Centered>
      <h1>{t('privacy.restore.title')}</h1>
      {!token ? (
        <Notice kind="error">{t('auth.verify.missingToken')}</Notice>
      ) : (
        <>
          <p>{t('privacy.restore.text')}</p>
          {restore.isError ? <Notice kind="error">{errorMessage(restore.error, t)}</Notice> : null}
          <Button size="lg" busy={restore.isPending} onClick={() => restore.mutate()}>
            {t('privacy.restore.button')}
          </Button>
        </>
      )}
    </Centered>
  );
}

/**
 * /export?token=… from the export e-mail (signed in; privacy.html, "download" and "downloaded"):
 * downloads the JSON file. Neither its size nor the link's end are known before (decision 3 of
 * SP-UX10.6).
 */
export function ExportDownload() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const url = useRef<string | null>(null);
  const download = useMutation({
    mutationFn: async () => {
      const data = await unwrap(api.POST('/account/export/download', { body: { token } }));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      if (url.current) URL.revokeObjectURL(url.current);
      url.current = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url.current;
      // The brand's name as the API's download header has it: siteprio-export-<day>.json.
      const brand = t('app.name')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
      a.download = `${brand}-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
    },
  });
  useEffect(
    () => () => {
      if (url.current) URL.revokeObjectURL(url.current);
    },
    [],
  );
  if (download.isSuccess)
    return (
      <Centered>
        <BigDoneIcon />
        <DoneTitle>{t('privacy.download.doneTitle')}</DoneTitle>
        <LinkButton to="/account" variant="secondary">
          {t('privacy.download.toAccount')}
        </LinkButton>
      </Centered>
    );
  return (
    <Centered>
      <h1>{t('privacy.download.title')}</h1>
      {!token ? (
        <Notice kind="error">{t('auth.verify.missingToken')}</Notice>
      ) : (
        <>
          <p>{t('privacy.download.text')}</p>
          {download.isError ? (
            <Notice kind="error">{errorMessage(download.error, t)}</Notice>
          ) : null}
          <Button size="lg" busy={download.isPending} onClick={() => download.mutate()}>
            {t('privacy.download.button')}
          </Button>
        </>
      )}
    </Centered>
  );
}
